import type { FC } from 'react';
import { useRef } from 'react';
import { extendAnalyticsData, noop, selectUserIsAuthenticated, sendAnalyticsData, useLocalStorage, useResizeDetector, useSwitchValue, useTimeoutFn } from '@mwl/core-lib';
import { useTypedSelector } from '@/hooks';
import { useFormSettings, usePromoField } from './PromoField.hooks';
import type { PromoFieldFormProps, PromoFieldFormRenderProps } from './PromoFieldForm.types';
import { getTimeoutValue } from './PromoFieldForm.utils';
export const PromoFieldForm: FC<PromoFieldFormProps> = ({
  mode,
  children,
  currency,
  maxLength,
  disableInitialTooltip,
  analytics,
  ...props
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const {
    width: formWidth
  } = useResizeDetector({
    targetRef: rootRef,
    onResize: noop
  });
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const [wasInfoClosed, setWasInfoClosed] = useLocalStorage<boolean>('promo-info-tooltip-closed', {
    defaultValue: false
  });
  const {
    value: isTooltipOpen,
    on: openTooltip,
    off: closeTooltip
  } = useSwitchValue(false);
  const formSettings = useFormSettings({
    maxLength
  });
  const promoField = usePromoField({
    mode,
    currency,
    formSettings,
    onError: promocode => {
      const fullAnalytics = extendAnalyticsData(analytics, {
        promocode
      });
      sendAnalyticsData(fullAnalytics?.error?.eventName, fullAnalytics?.error?.data);
      openTooltip();
    },
    onSuccess: promocode => {
      const fullAnalytics = extendAnalyticsData(analytics, {
        promocode
      });
      sendAnalyticsData(fullAnalytics?.success?.eventName, fullAnalytics?.success?.data);
      openTooltip();
    },
    onBeforeSubmit: () => {
      if (!wasInfoClosed && isAuth) {
        setWasInfoClosed(true);
      }
      closeTooltip();
    }
  });
  const {
    form,
    mutationState,
    doOnSubmit,
    resetSuccess,
    resetData,
    resetErrors
  } = promoField;
  const {
    handleSubmit,
    resetForm
  } = form;
  useTimeoutFn(closeTooltip, {
    timeout: getTimeoutValue(mode, mutationState),
    enabled: isTooltipOpen,
    onFinished: () => {
      if (mutationState.error) {
        resetErrors();
      }
      if (mutationState.isSuccess) {
        resetSuccess();
        resetForm(formSettings);
      }
      if (isAuth) {
        setWasInfoClosed(true);
      }
    }
  });
  const handleFormChange = () => {
    if (wasInfoClosed) {
      closeTooltip();
    }
    if (mutationState.isSuccess) {
      resetData();
      resetSuccess();
    }
  };
  const renderProps: PromoFieldFormRenderProps = {
    formWidth,
    isTooltipOpen,
    openTooltip,
    closeTooltip,
    onSubmit: handleSubmit(doOnSubmit),
    ...promoField
  };
  return <div {...props} tabIndex={-1} ref={rootRef} onFocus={wasInfoClosed || disableInitialTooltip ? noop : openTooltip} onChange={handleFormChange} data-sentry-component="PromoFieldForm" data-sentry-source-file="PromoFieldForm.tsx">
      {children(renderProps)}
    </div>;
};
export * from './PromoFieldForm.types';